import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  FormField,
  GhostButton,
  IconButton,
  Input,
  PlusIcon,
  SecondaryButton,
  Select,
  Toggle,
  TrashIcon,
} from "../../../design-system";
import { EyeIcon, EyeOffIcon } from "../../../design-system/icons";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { adminAutomationRenderPreview } from "../../utils/adminApi";
import VariableTextArea from "./VariableTextArea";
import { templateHasVariables } from "./templateVariables";
import {
  CHANNEL_META,
  EMAIL_VARIANT_META,
  MAX_ACTION_BODY_LENGTH,
  MAX_ACTION_TITLE_LENGTH,
  daysHoursToMinutes,
  emptyAction,
  formatDelay,
  formatEmailVariant,
} from "./automationUtils";

// Headings inside the message preview follow the design-system title rules
// (font-heading font-bold) — see TYPOGRAPHY_AND_THEME.md "ReactMarkdown overrides".
const markdownComponents = {
  h1: ({ children }) => <h1 className={`font-heading text-xl font-bold ${textColors.primary} mt-3 mb-2 first:mt-0`}>{children}</h1>,
  h2: ({ children }) => <h2 className={`font-heading text-lg font-bold ${textColors.primary} mt-3 mb-2 first:mt-0`}>{children}</h2>,
  h3: ({ children }) => <h3 className={`font-heading text-base font-bold ${textColors.primary} mt-3 mb-1 first:mt-0`}>{children}</h3>,
  h4: ({ children }) => <h4 className={`font-heading text-base font-bold ${textColors.primary} mt-2 mb-1 first:mt-0`}>{children}</h4>,
  h5: ({ children }) => <h5 className={`font-heading text-sm font-bold ${textColors.primary} mt-2 mb-1 first:mt-0`}>{children}</h5>,
  h6: ({ children }) => <h6 className={`font-heading text-sm font-bold ${textColors.primary} mt-2 mb-1 first:mt-0`}>{children}</h6>,
  p: ({ children }) => <p className={`${typography.bodySmall} ${textColors.secondary} mb-2 last:mb-0`}>{children}</p>,
  a: ({ href, children }) => (
    <a href={href} className={typography.link} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className={`list-disc pl-5 mb-2 ${typography.bodySmall} ${textColors.secondary}`}>{children}</ul>,
  ol: ({ children }) => <ol className={`list-decimal pl-5 mb-2 ${typography.bodySmall} ${textColors.secondary}`}>{children}</ol>,
  li: ({ children }) => <li className="mb-0.5">{children}</li>,
  strong: ({ children }) => <strong className={`font-semibold ${textColors.primary}`}>{children}</strong>,
  code: ({ children }) => (
    <code className={`px-1 py-0.5 rounded ${surfaces.tertiary} ${textColors.primary} text-xs`}>{children}</code>
  ),
};

const RENDER_DEBOUNCE_MS = 600;

/**
 * Debounced "how it will look" rendering of the title/body for a real sample
 * user (the backend renders the variables — it is the source of truth for
 * formats). Skips the network when the text has no variables.
 */
const useRenderedSample = (title, body) => {
  const [state, setState] = useState({ status: "idle", data: null, error: "" });
  const hasVariables = templateHasVariables(title) || templateHasVariables(body);
  useEffect(() => {
    if (!hasVariables) {
      setState({ status: "idle", data: null, error: "" });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, status: "loading", error: "" }));
    const timer = setTimeout(async () => {
      try {
        const data = await adminAutomationRenderPreview({ title, body_markdown: body });
        if (!cancelled) setState({ status: "success", data, error: "" });
      } catch (err) {
        if (!cancelled) setState((prev) => ({ status: "error", data: prev.data, error: err?.message || "Unknown error" }));
      }
    }, RENDER_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [title, body, hasVariables]);
  return { ...state, hasVariables };
};

const MessagePreview = ({ action, defaultSender }) => {
  const variant = action.email_template_variant || "default";
  const rawTitle = String(action.title || "");
  const rawBody = String(action.body_markdown || "");
  const sample = useRenderedSample(rawTitle, rawBody);
  const rendered = sample.status === "success" && sample.data ? sample.data : null;
  const shownTitle = rendered ? String(rendered.title || "") : rawTitle;
  const shownBody = rendered ? String(rendered.body_markdown || "") : rawBody;
  const variantMeta = EMAIL_VARIANT_META[variant] || { label: formatEmailVariant(variant), hint: "" };
  const emailOn = Boolean(action.channels?.email);
  const inboxOn = Boolean(action.channels?.inbox);
  const sender = String(action.sender_display_name || "").trim() || defaultSender || "—";
  const delayMinutes = daysHoursToMinutes(action.delay_days, action.delay_hours);

  return (
    <div className={`rounded-2xl border ${borders.default} ${surfaces.primary} overflow-hidden`} aria-label="Message preview">
      <div className={`px-4 py-2 border-b ${borders.light} ${surfaces.secondary} flex flex-wrap items-center gap-x-4 gap-y-1`}>
        <span className={`${typography.caption} ${textColors.tertiary} uppercase tracking-wider`}>Preview</span>
        <span className={`${typography.caption} ${textColors.secondary}`}>
          Channels: {[inboxOn ? "Inbox" : null, emailOn ? "Email" : null].filter(Boolean).join(" + ") || "none"}
        </span>
        {emailOn ? (
          <span className={`${typography.caption} ${textColors.secondary}`}>Email template: {variantMeta.label}</span>
        ) : null}
        <span className={`${typography.caption} ${textColors.secondary}`}>Delay: {formatDelay(delayMinutes)}</span>
      </div>
      <div className="p-4 space-y-3">
        {emailOn && variantMeta.hint ? (
          <p className={`${typography.caption} ${textColors.tertiary} italic`}>{variantMeta.hint}</p>
        ) : null}
        <div className={`${typography.caption} ${textColors.tertiary}`}>
          From <span className={`${textColors.primary} font-medium`}>{sender}</span>
        </div>
        {sample.hasVariables ? (
          <div
            className={`rounded-xl border ${borders.light} ${surfaces.secondary} px-3 py-2 ${typography.caption} ${textColors.secondary}`}
            role="status"
            aria-live="polite"
            data-testid="rendered-sample-status"
          >
            {sample.status === "loading" && !rendered ? "Rendering the variables for a sample user…" : null}
            {sample.status === "error" ? `Could not render the variables: ${sample.error}` : null}
            {rendered && rendered.sample_user ? (
              <>
                Variables rendered for sample user <span className={`font-medium ${textColors.primary}`}>{rendered.sample_user.username}</span>
                {sample.status === "loading" ? " · updating…" : ""}
                {Array.isArray(rendered.warnings) && rendered.warnings.length ? (
                  <span className="block text-red-700 dark:text-red-300">{rendered.warnings.join(" ")}</span>
                ) : null}
              </>
            ) : null}
            {rendered && !rendered.sample_user ? "No user available to render a sample — showing the raw template." : null}
          </div>
        ) : null}
        <div className={`font-heading text-base font-bold ${textColors.primary} break-words`}>
          {shownTitle.trim() || <span className={`${textColors.muted} font-normal`}>(no title yet)</span>}
        </div>
        <div className="break-words">
          {shownBody.trim() ? (
            <ReactMarkdown components={markdownComponents}>{shownBody}</ReactMarkdown>
          ) : (
            <p className={`${typography.bodySmall} ${textColors.muted}`}>(no body yet)</p>
          )}
        </div>
        {emailOn && variant === "marketing" ? (
          <p className={`pt-2 border-t ${borders.light} ${typography.caption} ${textColors.muted}`}>
            Marketing emails end with the unsubscribe footer.
          </p>
        ) : null}
      </div>
    </div>
  );
};

MessagePreview.propTypes = {
  action: PropTypes.object.isRequired,
  defaultSender: PropTypes.string,
};

const ActionCard = ({
  action,
  index,
  total,
  variants,
  variables,
  defaultSender,
  onChange,
  onRemove,
  onMove,
  disabled,
  errors,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const idp = `action-${action._key || index}`;
  const emailOn = Boolean(action.channels?.email);
  const delayMinutes = daysHoursToMinutes(action.delay_days, action.delay_hours);
  const variantOptions = (variants.length ? variants : Object.keys(EMAIL_VARIANT_META)).map((v) => ({
    value: v,
    label: formatEmailVariant(v),
  }));
  const variantHint = EMAIL_VARIANT_META[action.email_template_variant]?.hint || "";
  const channelError = errors?.[`actions.${index}.channels`] || "";

  return (
    <li className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`} aria-label={`Action ${index + 1}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className={`${typography.label} ${textColors.primary}`}>
            Action {index + 1}
            <span className={`ml-2 ${typography.caption} ${textColors.tertiary}`}>{formatDelay(delayMinutes)}</span>
          </div>
          <div className={`${typography.caption} ${textColors.tertiary}`}>
            {index === 0 ? "Sent when the user enters the situation" : "Follow-up message"}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <IconButton
            size="sm"
            variant="ghost"
            shape="square"
            icon={<ChevronUpIcon size="sm" />}
            aria-label={`Move action ${index + 1} up`}
            title="Move up"
            onClick={() => onMove(index, index - 1)}
            disabled={disabled || index === 0}
          />
          <IconButton
            size="sm"
            variant="ghost"
            shape="square"
            icon={<ChevronDownIcon size="sm" />}
            aria-label={`Move action ${index + 1} down`}
            title="Move down"
            onClick={() => onMove(index, index + 1)}
            disabled={disabled || index === total - 1}
          />
          <IconButton
            size="sm"
            variant="ghost"
            shape="square"
            icon={<TrashIcon size="sm" />}
            aria-label={`Remove action ${index + 1}`}
            title={total === 1 ? "At least one action is required" : "Remove action"}
            onClick={() => onRemove(index)}
            disabled={disabled || total === 1}
          />
        </div>
      </div>

      <div className="space-y-4">
        {/* Channels */}
        <div>
          <span className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
            Channels <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
          </span>
          <div className="flex flex-wrap gap-x-6 gap-y-2" role="group" aria-label={`Action ${index + 1} channels`}>
            {Object.entries(CHANNEL_META).map(([channel, meta]) => (
              <Toggle
                key={channel}
                id={`${idp}-channel-${channel}`}
                checked={Boolean(action.channels?.[channel])}
                onChange={(e) =>
                  onChange({ channels: { ...(action.channels || {}), [channel]: Boolean(e?.target?.checked) } })
                }
                label={meta.label}
                description={meta.description}
                labelPosition="right"
                size="sm"
                disabled={disabled}
              />
            ))}
          </div>
          {channelError ? (
            <p className={`mt-1 ${typography.caption} text-red-600 dark:text-red-400`} role="alert">
              {channelError}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Email template" htmlFor={`${idp}-variant`}>
            <Select
              id={`${idp}-variant`}
              value={action.email_template_variant || "default"}
              onChange={(e) => onChange({ email_template_variant: e.target.value })}
              options={variantOptions}
              placeholder={null}
              disabled={disabled || !emailOn}
              helperText={emailOn ? variantHint : "Only used when the Email channel is on."}
            />
          </FormField>

          <FormField label="Sender display name" htmlFor={`${idp}-sender`} optional>
            <Input
              id={`${idp}-sender`}
              value={action.sender_display_name || ""}
              onChange={(e) => onChange({ sender_display_name: e.target.value })}
              placeholder={defaultSender || "Sender name"}
              disabled={disabled}
              helperText={`Shown as the sender in the inbox and email.${defaultSender ? ` Blank = "${defaultSender}".` : ""}`}
            />
          </FormField>
        </div>

        <FormField label="Title" htmlFor={`${idp}-title`} required>
          <VariableTextArea
            id={`${idp}-title`}
            singleLine
            value={action.title || ""}
            onChange={(title) => onChange({ title })}
            variables={variables}
            maxLength={MAX_ACTION_TITLE_LENGTH}
            placeholder="e.g. {{first_name}}, your plan ends in a few days"
            disabled={disabled}
            error={errors?.[`actions.${index}.title`] || ""}
            helperText="Inbox title and email subject. Type @ to insert a variable."
          />
        </FormField>

        <FormField label="Body" htmlFor={`${idp}-body`} required>
          <VariableTextArea
            id={`${idp}-body`}
            value={action.body_markdown || ""}
            onChange={(body_markdown) => onChange({ body_markdown })}
            variables={variables}
            rows={6}
            maxLength={MAX_ACTION_BODY_LENGTH}
            placeholder={"Write the message… Markdown supported, e.g. **bold**, _italic_, [link](https://myvitae.ai). Type @ for variables."}
            disabled={disabled}
            error={errors?.[`actions.${index}.body_markdown`] || ""}
            helperText="Markdown supported — **bold**, _italic_, lists and links. Type @ to insert a variable (e.g. {{paid_plan_expiration_date - YYYY-MM-DD}})."
          />
        </FormField>

        <div>
          <span className={`block mb-2 ${typography.label} ${textColors.secondary}`}>Delay</span>
          <div className="grid grid-cols-2 gap-3 max-w-xs">
            <FormField label="Days" htmlFor={`${idp}-delay-days`}>
              <Input
                id={`${idp}-delay-days`}
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={String(action.delay_days ?? "0")}
                onChange={(e) => onChange({ delay_days: e.target.value })}
                disabled={disabled}
                error={errors?.[`actions.${index}.delay_days`] || ""}
                size="sm"
              />
            </FormField>
            <FormField label="Hours" htmlFor={`${idp}-delay-hours`}>
              <Input
                id={`${idp}-delay-hours`}
                type="number"
                inputMode="numeric"
                min={0}
                max={23}
                step={1}
                value={String(action.delay_hours ?? "0")}
                onChange={(e) => onChange({ delay_hours: e.target.value })}
                disabled={disabled}
                error={errors?.[`actions.${index}.delay_hours`] || ""}
                size="sm"
              />
            </FormField>
          </div>
          <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
            Delivered <strong>no earlier than</strong> this long after the user enters the situation ({formatDelay(delayMinutes)}).
            0 = with the next delivery cycle.
          </p>
        </div>

        <div>
          <GhostButton
            size="sm"
            onClick={() => setShowPreview((v) => !v)}
            leftIcon={showPreview ? <EyeOffIcon size="sm" /> : <EyeIcon size="sm" />}
            aria-expanded={showPreview}
            aria-controls={`${idp}-preview`}
          >
            {showPreview ? "Hide preview" : emailOn ? "Preview email" : "Preview message"}
          </GhostButton>
          {showPreview ? (
            <div id={`${idp}-preview`} className="mt-3">
              <MessagePreview action={action} defaultSender={defaultSender} />
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
};

ActionCard.propTypes = {
  action: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  variants: PropTypes.arrayOf(PropTypes.string).isRequired,
  variables: PropTypes.array,
  defaultSender: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

/**
 * Add / remove / reorder the messages an automation sends (1..n). Each action
 * picks its channels (inbox and/or email), the email template variant, the
 * sender name, title, Markdown body and a "no earlier than" delay.
 */
const ActionsEditor = ({ actions = [], variants = [], variables = [], defaultSender = "", onChange, disabled = false, errors = {} }) => {
  const updateAction = (index, patch) => onChange(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  const removeAction = (index) => {
    if (actions.length <= 1) return;
    onChange(actions.filter((_, i) => i !== index));
  };
  const moveAction = (from, to) => {
    if (to < 0 || to >= actions.length) return;
    const next = [...actions];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const addAction = () => onChange([...actions, emptyAction(defaultSender)]);

  return (
    <div className="space-y-3">
      {errors?.actions ? (
        <p className={`${typography.caption} text-red-600 dark:text-red-400`} role="alert">
          {errors.actions}
        </p>
      ) : null}
      <ol className="space-y-3">
        {actions.map((action, index) => (
          <ActionCard
            key={action._key || index}
            action={action}
            index={index}
            total={actions.length}
            variants={variants}
            variables={variables}
            defaultSender={defaultSender}
            onChange={(patch) => updateAction(index, patch)}
            onRemove={removeAction}
            onMove={moveAction}
            disabled={disabled}
            errors={errors}
          />
        ))}
      </ol>
      <SecondaryButton size="sm" onClick={addAction} disabled={disabled} leftIcon={<PlusIcon size="sm" />}>
        Add action
      </SecondaryButton>
    </div>
  );
};

ActionsEditor.propTypes = {
  actions: PropTypes.array,
  variants: PropTypes.arrayOf(PropTypes.string),
  /** Message variables from the conditions meta (key, label, type, formats, …). */
  variables: PropTypes.array,
  defaultSender: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** field → message, keys like "actions", "actions.0.title" */
  errors: PropTypes.object,
};

export default ActionsEditor;
